import React, { Fragment, useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Form, FormGroup, Label, Table, Spinner, Badge } from 'reactstrap';
import Api from '../../api/api';
import OverlayLoading from '../common/overlay-loading';

const CsvUpload = () => {
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);

  useEffect(() => {
    fetchFiles();
    const intervalId = setInterval(fetchFiles, 10000);
  
    return () => clearInterval(intervalId);
  }, []);

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!file) {
      alert('Please select a file to upload.');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file, file.name);

      const response = await Api.postFiles('files', formData);

      if (!response.error) {
        fetchFiles();
        toggleModal();
      } else {
        alert(`Failed to upload file. Error: ${response.error}`);
      }
    } catch (error) {
      console.error(error);
      alert('Failed to upload file.');
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await Api.get('files');
      if (!response.error) {
        setFiles(response.data);
      } else {
        alert(`Failed to fetch files. Error: ${response.error}`);
      }
    } catch (error) {
      console.error(error);
      alert('Failed to fetch files.');
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    setFile(null);
  };

  const downloadFile = async (file) => {
    try {
      const response = await Api.download(`files/${file.id}`);
      if (response) {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      } else {
        alert('Failed to download file.');
      }
    } catch (error) {
      console.error('Client-side error:', error);
      alert('Failed to download file.');
    }
  };

  const deleteFile = async (id) => {
    if (!window.confirm("Are you sure you want to delete this file?")) {
      return;
    }

    setLoading(true);
    try {
      const response = await Api.del(`files/${id}`);
      if (!response.error) {
        fetchFiles();
      } else {
        alert(`Failed to delete file. Error: ${response.error}`);
      }
    } catch (error) {
      console.error(error);
      alert('Failed to delete file.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge color="warning"><Spinner size="sm" /> Pending</Badge>;
      case 'processed':
        return <Badge color="success">Completed</Badge>;
      case 'error':
        return <Badge color="danger">Failed</Badge>;
      default:
        return <Badge color="secondary">Unknown</Badge>;
    }
  }

  return (
    <Fragment>
      <OverlayLoading show={loading} />
      <div className="container-fluid pt-5">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col">
                    <h5>Upload CSV</h5>
                  </div>
                  <div className="col">
                    <div className="pull-right right-header">
                      <Button color="primary" onClick={toggleModal}>Upload New File</Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {files.length > 0 && (
                  <Table striped>
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>Created</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {files.map(file => (
                        <tr key={file.fileName}>
                          <td>{file.fileName}</td>
                          <td>{new Date(file.created).toLocaleString()}</td>
                          <td>{getStatusBadge(file.status)}</td>
                          <td>
                            <Button color="success" size="sm" onClick={() => downloadFile(file)}>Download</Button>{' '}
                            <Button color="danger" size="sm" onClick={() => deleteFile(file.id)}>Delete</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal isOpen={modal} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>Upload File</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label for="csvFile">CSV File</Label>
              <Input
                type="file"
                name="file"
                id="csvFile"
                accept=".csv"
                onChange={handleFileChange}
              />
            </FormGroup>
            <ModalFooter>
              <Button color="primary" type="submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Upload'}
              </Button>
              <Button color="secondary" onClick={toggleModal}>Cancel</Button>
            </ModalFooter>
          </Form>
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default CsvUpload;
